import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, formData);
      
      // Store token
      localStorage.setItem("ptv_admin_token", response.data.token);
      localStorage.setItem("ptv_admin_email", response.data.email);
      
      toast.success("Welcome back!");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6" data-testid="admin-login-page">
      {/* Background Pattern */}
      <div className="fixed inset-0 opacity-5">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #D4AF37 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Back to Home */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/50 hover:text-[#D4AF37] transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Prestigious TV
        </Link>

        {/* Login Card */}
        <div className="card p-10 border border-white/10">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="font-['Playfair_Display'] text-3xl font-bold">
              Prestigious<span className="text-[#D4AF37]">TV</span>
            </h1>
            <p className="text-white/50 mt-2">Admin Portal</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="block text-white/70 text-sm mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="input-dark w-full pl-11"
                  placeholder="admin@ptv.com"
                  data-testid="admin-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/70 text-sm mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="input-dark w-full pl-11"
                  placeholder="••••••••"
                  data-testid="admin-password"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="btn-primary w-full"
              data-testid="admin-login-submit"
            >
              {loading ? "Authenticating..." : "Sign In"}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="text-white/40 text-sm">
              Secure admin access only
            </p>
          </div>
        </div>

        {/* Bottom text */}
        <p className="text-center text-white/30 text-xs mt-6">
          © 2026 Prestigious-TV Admin Portal
        </p>
      </div>
    </div>
  );
}
